/* React base class */
import { Component } from "react"

/* Redux store connection */
import { connect } from "react-redux"

/* Router HOC and shared utilities */
import { withRouter, utils, applySeo } from "../../helpers"

/* Shared UI components */
import { Account, Loading, Network, Stars } from "../../components"

/* Icon component */
import Icon from "../../components/Icon"

/* Review API: create and delete */
import { create, remove } from "../../api/Review"

/* Profile API: the user's reviews and completed bookings awaiting a review */
import { reviews, pendingReviews } from "../../api/Me"

/* Organization API: the team of the point — the review names the master */
import { specialists } from "../../api/Organization"

/* Review modal and photo lightbox */
import LeaveReview from "../../widgets/organization/LeaveReview"
import Lightbox from "../../widgets/organization/Lightbox"

/* i18n */
import { translate } from "../../translates"

/* Page Reviews */
class Reviews extends Component {

    constructor(props) {
        super(props)

        /* State */
        this.state = {
            loading: true,
            error: false,
            tab: "mine",
            mine: [],
            pending: [],
            confirm: null,
            busy: false,
            leave: null,
            toast: null,
            lightbox: null
        }
    }

    componentDidMount = () => {

        /* SEO */
        applySeo({ title: `Мои отзывы — Bookify`, description: `Ваши отзывы о заведениях в Bookify.`, path: `/reviews`, robots: `noindex, nofollow` })

        /* Scroll */
        window.scroll(0, 0)

        /* Load */
        this.load()
    }

    /* Draw Toast */
    _toast = (message) => {

        /* Show a short auto-hiding message */
        this.setState({ toast: message })
        setTimeout(() => this.setState({ toast: null }), 2600)
    }

    /* Draw Load */
    load = () => {

        /* Fields */
        const { token } = this.props

        /* Load the user's reviews and pending ones together */
        this.setState({ loading: true, error: false })
        Promise.all([reviews(token), pendingReviews(token)]).then(([responseMine, responsePending]) => {
            if (responseMine && responseMine.status === 200) {
                this.setState({
                    mine: Array.isArray(responseMine.data) ? responseMine.data : [],
                    pending: responsePending && responsePending.status === 200 && Array.isArray(responsePending.data) ? responsePending.data : [],
                    loading: false
                })
            }
            else {
                this.setState({ loading: false, error: true })
            }
        })
    }

    /* Draw Open Leave */
    _openLeave = (target) => {

        /* Open the review modal (target: { name, organizationId, initial }) */
        this.setState({ leave: target })
    }

    /* Draw Close Leave */
    _closeLeave = () => {

        /* Dismiss the review modal */
        this.setState({ leave: null })
    }

    /* Draw Start Edit */
    _startEdit = (review) => {

        /* Open the modal pre-filled with the existing review */
        const photos = (Array.isArray(review.photos) ? review.photos : []).map((path) => ({ id: path.split("/").pop(), url: utils.media(path) }))
        const initial = { rating: review.rate || review.rating, text: review.comment || review.text || "", masterId: review.masterId, photos }

        /* The team of the point comes with the form — an old review without a master gets one,
           and the master already named stands chosen */
        specialists(review.organizationId).then((response) => {
            const team = response && response.status === 200 && Array.isArray(response.data) ? response.data : []
            this._openLeave({ name: review.organizationName, organizationId: review.organizationId, specialists: team, initial })
        })
    }

    /* Draw Submit Review */
    _submitReview = (data) => {

        /* Fields */
        const { leave } = this.state
        const { token } = this.props

        /* Create or update the review (upsert by organization) */
        return create(token, { organizationId: leave.organizationId, memberId: data.memberId, rating: data.rating, text: data.text, photoIds: data.photoIds }).then((response) => {
            if (response && (response.status === 200 || response.status === 201)) {
                this.setState({ leave: null })
                this.load()
                this._toast(leave.initial ? translate("Отзыв обновлён") : translate("Отзыв опубликован"))
            }
            else {
                this._toast(translate("Не удалось сохранить отзыв"))
            }
        })
    }

    /* Draw Ask Delete */
    _askDelete = (id) => {

        /* Confirm before deleting */
        this.setState({ confirm: id })
    }

    /* Draw Close Confirm */
    _closeConfirm = () => {

        /* Dismiss the confirmation modal */
        this.setState({ confirm: null })
    }

    /* Draw Confirm Delete */
    _confirmDelete = () => {

        /* Fields */
        const { confirm, busy, mine } = this.state
        const { token } = this.props

        /* Delete the review */
        if (busy) {
            return
        }
        this.setState({ busy: true })
        remove(token, confirm).then((response) => {
            this.setState({ busy: false })
            if (response && (response.status === 204 || response.status === 200)) {
                this.setState({ confirm: null, mine: mine.filter((item) => item.id !== confirm) })
            }
        })
    }

    /* Draw Confirm Modal */
    _confirmModal = () => {

        /* Fields */
        const { confirm, busy } = this.state

        /* Nothing to confirm */
        if (!confirm) {
            return null
        }

        return (
            <div className="modal-backdrop" onClick={this._closeConfirm}>
                <div className="modal modal--sm" onClick={(event) => event.stopPropagation()}>
                    <div className="modal__body">
                        <div className="booking-cancel">
                            <div className="booking-cancel__icon"><Icon name="trash" size={28} /></div>
                            <h3 className="booking-cancel__title">{translate("Удалить отзыв?")}</h3>
                            <p className="booking-cancel__text">{translate("Отзыв будет удалён без возможности восстановления.")}</p>
                            <div className="booking-cancel__actions">
                                <button className="btn btn--ghost btn--lg" disabled={busy} onClick={this._closeConfirm}>{translate("Отменить")}</button>
                                <button className="btn btn--danger btn--lg" disabled={busy} onClick={this._confirmDelete}>{busy ? translate("Удаляется…") : translate("Удалить")}</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    /* Draw Mine */
    _mine = () => {

        /* Fields */
        const { mine } = this.state

        /* Empty */
        if (mine.length === 0) {
            return (
                <div className="empty">
                    <div className="empty__art"><Icon name="chat" size={48} /></div>
                    <h3>{translate("Вы пока не оставили отзывов")}</h3>
                    <p>{translate("Поделитесь впечатлениями после визита.")}</p>
                </div>
            )
        }

        /* List */
        return mine.map((review) => (
            <div key={`${review.id}`} className="myreview">
                <div className="myreview__head">
                    {review.organizationImage ? <img src={utils.media(review.organizationImage)} alt="" /> : <span className="avatar">{(review.organizationName || "?")[0]}</span>}
                    <div>
                        <div className="myreview__organization">{review.organizationName}</div>
                        <div className="myreview__service">{review.service || ""}</div>
                    </div>
                    <span className="myreview__date">{utils.reviewDate(review.createdAt)}</span>
                </div>
                <div className="myreview__stars"><Stars value={review.rate || review.rating} size={16} /></div>
                <p className="myreview__text">{review.comment || review.text}</p>
                {Array.isArray(review.photos) && review.photos.length > 0 && (
                    <div className="myreview__photos">
                        {review.photos.map((path, index) => <img key={`${index}`} src={utils.media(path)} alt="" onError={utils.onImgError} onClick={() => this.setState({ lightbox: { images: review.photos, index } })} />)}
                    </div>
                )}
                {review.reply && (
                    <div className="myreview__reply">
                        <div className="myreview__reply-head">
                            <span className="myreview__reply-label">{translate("Ответ организации")}</span>
                            {review.replyDate && <span className="myreview__reply-date">{utils.reviewDate(review.replyDate)}</span>}
                        </div>
                        <p className="myreview__reply-text">{review.reply}</p>
                    </div>
                )}
                <div className="myreview__actions">
                    <button className="btn btn--ghost reviews__action" onClick={() => this._startEdit(review)}>
                        <Icon name="edit" size={14} /> {translate("Редактировать")}
                    </button>
                    <button className="btn btn--danger-ghost reviews__action" onClick={() => this._askDelete(review.id)}>
                        <Icon name="trash" size={14} /> {translate("Удалить")}
                    </button>
                </div>
            </div>
        ))
    }

    /* Draw Pending */
    _pending = () => {

        /* Fields */
        const { pending } = this.state

        /* Empty */
        if (pending.length === 0) {
            return (
                <div className="empty">
                    <div className="empty__art"><Icon name="chat" size={48} /></div>
                    <h3>{translate("Нет записей для отзыва")}</h3>
                    <p>{translate("Завершённые записи появятся здесь.")}</p>
                </div>
            )
        }

        /* List */
        return pending.map((booking) => (
            <div key={`${booking.id}`} className="myreview">
                <div className="myreview__head reviews__pending-head">
                    {booking.organizationImage ? <img src={utils.media(booking.organizationImage)} alt="" /> : <span className="avatar">{(booking.organizationName || "?")[0]}</span>}
                    <div>
                        <div className="myreview__organization">{booking.organizationName}</div>
                        <div className="myreview__service">{booking.service}</div>
                    </div>
                </div>
                <div className="reviews__cta">
                    <div>
                        <div className="reviews__cta-title">{translate("Как прошёл визит?")}</div>
                        <div className="reviews__cta-text">{translate("Оцените мастера и оставьте отзыв")}</div>
                    </div>
                    <button className="btn" onClick={() => this._openLeave({ name: booking.organizationName, organizationId: booking.organizationId, master: { id: booking.masterId, name: booking.masterName }, initial: null })}>{translate("Оставить отзыв")}</button>
                </div>
            </div>
        ))
    }

    /* Draw Content */
    _content = () => {

        /* Fields */
        const { loading, error, tab } = this.state

        /* Loading */
        if (loading) {
            return <Loading inline />
        }

        /* Error */
        if (error) {
            return <Network onRetry={this.load} />
        }

        /* Tab */
        if (tab === "mine") {
            return this._mine()
        }
        return this._pending()
    }

    render = () => {

        /* Fields */
        const { tab, mine, pending, leave, toast, lightbox } = this.state

        return (
            <Account active="reviews">
                <div className="content">
                    <h2 className="content__title">{translate("Мои отзывы")}</h2>
                    <p className="content__subtitle">{translate("Поделитесь впечатлениями — это помогает другим")}</p>

                    <div className="tabs">
                        <button className={`tab ${tab === "mine" ? "is-active" : ""}`} onClick={() => this.setState({ tab: "mine" })}>{translate("Оставленные")} · {mine.length}</button>
                        <button className={`tab ${tab === "pending" ? "is-active" : ""}`} onClick={() => this.setState({ tab: "pending" })}>{translate("Ожидают отзыв")} · {pending.length}</button>
                    </div>

                    {this._content()}
                </div>

                {this._confirmModal()}

                {leave && (
                    <LeaveReview
                        name={leave.name}
                        token={this.props.token}
                        specialists={leave.specialists}
                        master={leave.master}
                        editing={!!leave.initial}
                        initial={leave.initial}
                        onClose={this._closeLeave}
                        onSubmit={this._submitReview}
                    />
                )}

                {toast && (
                    <div className="toast">
                        <div className="toast__check"><Icon name="check" size={16} /></div>
                        <div>{toast}</div>
                    </div>
                )}

                {lightbox && (
                    <Lightbox
                        images={lightbox.images}
                        startIndex={lightbox.index}
                        title={translate("Фото из отзыва")}
                        onClose={() => this.setState({ lightbox: null })}
                    />
                )}
            </Account>
        )
    }
}

/* Map State To Props */
const mapStateToProps = state => {
    return {
        token: state.token,
        lang: state.lang
    }
}

/* Export */
export default connect(mapStateToProps)(withRouter(Reviews))
